<template>
  <div class="km-page">
    <header class="km-top">
      <button class="km-back" @click="back()" aria-label="Back">
        <svg viewBox="0 0 24 24" width="20" height="20" fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round"><path d="M15 18l-6-6 6-6"/></svg>
      </button>
      <div class="km-top__title"><span class="km-top__dot"></span><h1>{{ $t('my.task_log') }}</h1></div>
      <div style="width:38px"></div>
    </header>

    <main class="km-main">
      <!-- Tabs -->
      <div class="kg-tabs">
        <button class="kg-tab" :class="{ active: activeTab === 'transactions' }" @click="onTabChange('transactions')">
          {{ $t('my.transactions') }}
        </button>
        <button class="kg-tab" :class="{ active: activeTab === 'tasks' }" @click="onTabChange('tasks')">
          {{ $t('my.task_records') }}
        </button>
      </div>

      <!-- Transactions -->
      <div v-if="activeTab === 'transactions'">
        <van-pull-refresh v-model="isLoading" @refresh="loadTransactions">
          <section class="km-section">
            <div v-if="txList.length === 0 && !isLoading" class="km-empty">
              <div class="km-empty__emoji">💸</div>
              <h3>{{ $t('my.no_transactions') }}</h3>
              <p>Your top-ups and withdrawals will appear here.</p>
            </div>
            <article v-for="(t, i) in txList" :key="i" class="kg-tx">
              <div class="kg-tx__head">
                <span class="kg-tx__kind">{{ t.kind }}</span>
                <span class="kg-tx__amount" :class="t.sign === '-' ? 'neg' : 'pos'">{{ t.sign }}{{ formatAmount(t.amount) }}</span>
              </div>
              <div class="kg-tx__meta">
                <span class="kg-tx__time">{{ t.time }}</span>
                <span class="kg-tx__pill" :class="'kg-tx__pill--' + t.status">{{ statusLabel(t.status) }}</span>
              </div>
              <div class="kg-tx__row" v-if="t.wallet_address">
                <span>To</span>
                <strong>{{ t.wallet_name }} · <em>{{ t.wallet_address }}</em></strong>
              </div>
              <div class="kg-tx__row" v-if="t.desc">
                <span>Note</span>
                <strong>{{ t.desc }}</strong>
              </div>
            </article>
          </section>
        </van-pull-refresh>
      </div>

      <!-- Tasks -->
      <div v-else>
        <van-pull-refresh v-model="isLoading" @refresh="loadTasks">
          <section class="km-section">
            <div v-if="list.length === 0 && !isLoading" class="km-empty">
              <div class="km-empty__emoji">📋</div>
              <h3>{{ $t('withdraw.empty_data') }}</h3>
              <p>Your lottery tasks and results will appear here.</p>
            </div>
            <article v-for="(v, key) in list" :key="key" class="kg-task">
              <div class="kg-task__head">
                <div class="kg-task__ico kg-task__fallback">🎰</div>
                <div class="kg-task__title">
                  <div class="kg-task__name">{{ v.name }}</div>
                  <div class="kg-task__expect">Round {{ v.expect }}</div>
                </div>
                <span class="kg-tx__pill" :class="verdictPillClass(v)">
                  {{ verdictLabel(v) }}
                </span>
              </div>
              <div class="kg-task__result">
                <span class="kg-task__lbl">{{ $t('reservation.result') }}</span>
                <span class="kg-result" :class="verdictPillClass(v)">
                  {{ verdictLabel(v) }}
                  <template v-if="v.is_win === 1"> · +{{ formatAmount(v.profit_total) }}</template>
                  <template v-else-if="v.is_win === 2"> · -{{ formatAmount(v.money) }}</template>
                </span>
                <span class="kg-task__money">{{ $t('reservation.money') }}: <strong>{{ v.money }}</strong></span>
              </div>
              <div class="kg-task__foot">
                <div><span>{{ $t('reservation.order_time') }}</span><strong>{{ v.create_time }}</strong></div>
                <div><span>{{ $t('reservation.settle_time') }}</span><strong>{{ v.update_time }}</strong></div>
              </div>
            </article>
          </section>
        </van-pull-refresh>
      </div>
    </main>
  </div>
</template>

<script>
export default {
  data() { return { activeTab: 'transactions', isLoading: false, list: [], txList: [], size: '', double: '' }; },
  methods: {
    back() { return window.history.back(); },
    formatAmount(n) { return (Number(n) || 0).toFixed(2); },
    statusLabel(s) {
      if (s === 'pending') return this.$t('my.pending');
      if (s === 'approved') return this.$t('my.approved');
      if (s === 'rejected') return this.$t('my.rejected');
      if (s === 'completed') return this.$t('my.completed');
      return s;
    },
    onTabChange(name) { this.activeTab = name; if (name === 'transactions') this.loadTransactions(); else this.loadTasks(); },
    verdictLabel(v) {
      if (v.is_win === 1) return 'WIN';
      if (v.is_win === 2) return 'LOSS';
      if (v.status === 1) return 'Settled';
      return 'Pending';
    },
    verdictPillClass(v) {
      if (v.is_win === 1) return 'kg-tx__pill--approved';
      if (v.is_win === 2) return 'kg-tx__pill--rejected';
      return 'kg-tx__pill--pending';
    },
    loadTransactions() {
      this.isLoading = true;
      this.$http({ method: 'get', url: 'user_transactions' }).then(res => {
        this.isLoading = false;
        if (res.code === 200) this.txList = res.data || [];
        else this.$toast(res.msg || 'Error');
      }).catch(() => { this.isLoading = false; });
    },
    loadTasks() {
      this.isLoading = true;
      this.$http({ method: 'get', url: 'user_get_game_list' }).then(res => {
        this.isLoading = false;
        if (res.code === 200) {
          this.list = res.data || [];
          this.list.forEach(item => {
            if (!item.opencode) return;
            const sum = (item.opencode[0]||0) + (item.opencode[1]||0) + (item.opencode[2]||0);
            if (sum >= 11 && sum <= 18) {
              this.size = this.$t('lottery_labels.rose');
            } else if (sum >= 3 && sum <= 10) {
              this.size = this.$t('lottery_labels.lily');
            }
            if (sum % 2 === 0) {
              this.double = this.$t('lottery_labels.balloon');
            } else {
              this.double = this.$t('lottery_labels.fireworks');
            }
          });
        } else if (res.code === 401) this.$toast(res.msg);
      }).catch(() => { this.isLoading = false; });
    }
  },
  created() {
    if (!localStorage.getItem('token')) this.$router.push({ path: '/Login' });
    else this.loadTransactions();
  }
};
</script>

<style lang="less" scoped>
@import "./_km-page.less";

.kg-tabs {
  position: sticky; top: 56px; z-index: 10;
  max-width: 640px; margin: 0 auto; padding: 10px 14px;
  display: flex; gap: 8px;
  background: rgba(253,245,249,.85); backdrop-filter: blur(20px);
}
.kg-tab {
  flex: 1; padding: 10px 16px; border: 1px solid rgba(26,16,51,.08);
  background: #fff; border-radius: 999px; cursor: pointer;
  color: #4a3d66; font-size: 13px; font-weight: 700;
  font-family: inherit;
  transition: all .2s;
}
.kg-tab:hover { border-color: #ff4d8d; }
.kg-tab.active {
  background: linear-gradient(90deg, #ff4d8d, #a020f0);
  color: #fff; border-color: transparent;
  box-shadow: 0 8px 20px rgba(255,77,141,.3);
}

.kg-tx {
  background: linear-gradient(180deg, #fff 0%, #fff6fa 100%);
  border-radius: 16px;
  border: 1px solid rgba(255,77,141,.12);
  box-shadow: 0 6px 18px rgba(255,77,141,.08), inset 0 1px 0 rgba(255,255,255,.9);
  padding: 14px 16px;
  margin-bottom: 12px;
}
.kg-tx__head { display: flex; align-items: center; justify-content: space-between; margin-bottom: 6px; }
.kg-tx__kind { font-size: 14px; font-weight: 800; color: #2a1a3f; }
.kg-tx__amount { font-size: 20px; font-weight: 800; letter-spacing: -.01em; }
.kg-tx__amount.pos { color: #10b981; }
.kg-tx__amount.neg { color: #ef4444; }
.kg-tx__meta { display: flex; align-items: center; justify-content: space-between; gap: 8px; padding-bottom: 8px; border-bottom: 1px solid rgba(255,77,141,.08); }
.kg-tx__time { font-size: 12px; color: #8273a0; }
.kg-tx__pill {
  font-size: 10px; font-weight: 800; padding: 3px 9px; border-radius: 999px;
  text-transform: uppercase; letter-spacing: .05em;
}
.kg-tx__pill--approved  { background: rgba(16,185,129,.15); color: #059669; }
.kg-tx__pill--completed { background: rgba(148,163,184,.2); color: #64748b; }
.kg-tx__pill--rejected  { background: rgba(239,68,68,.15); color: #dc2626; }
.kg-tx__pill--pending   { background: rgba(251,191,36,.18); color: #b45309; }
.kg-tx__row { display: flex; gap: 10px; margin-top: 8px; font-size: 12px; }
.kg-tx__row span { color: #8273a0; min-width: 42px; }
.kg-tx__row strong { color: #2a1a3f; font-weight: 600; word-break: break-all; }
.kg-tx__row em { color: #8273a0; font-style: normal; }

.kg-task {
  background: linear-gradient(180deg, #fff 0%, #fff6fa 100%);
  border-radius: 16px;
  border: 1px solid rgba(255,77,141,.12);
  box-shadow: 0 6px 18px rgba(255,77,141,.08), inset 0 1px 0 rgba(255,255,255,.9);
  padding: 14px 16px;
  margin-bottom: 12px;
}
.kg-task__head { display: flex; align-items: center; gap: 10px; margin-bottom: 10px; }
.kg-task__ico {
  width: 38px !important; height: 38px !important; border-radius: 10px !important; overflow: hidden;
  background: #faf3f8; flex-shrink: 0;
}
.kg-task__ico ::v-deep img { width: 100%; height: 100%; object-fit: cover; }
.kg-task__fallback { width: 100%; height: 100%; display: flex; align-items: center; justify-content: center; font-size: 20px; }
.kg-task__title { flex: 1; min-width: 0; }
.kg-task__name { font-size: 14px; font-weight: 800; color: #2a1a3f; letter-spacing: -.01em; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
.kg-task__expect { font-size: 11px; color: #8273a0; margin-top: 1px; }
.kg-task__result {
  display: flex; align-items: center; flex-wrap: wrap; gap: 8px;
  padding: 10px 0; border-top: 1px solid rgba(255,77,141,.08); border-bottom: 1px solid rgba(255,77,141,.08);
  font-size: 12px;
}
.kg-task__lbl { color: #8273a0; font-weight: 600; }
.kg-ball {
  padding: 3px 10px; border-radius: 999px;
  background: linear-gradient(90deg, #ff4d8d, #a020f0);
  color: #fff; font-size: 11px; font-weight: 700;
}
.kg-result {
  padding: 4px 11px; border-radius: 999px;
  font-size: 11px; font-weight: 800; letter-spacing: .04em;
  text-transform: uppercase;
}
.kg-result.kg-tx__pill--approved { background: rgba(16,185,129,.18); color: #059669; }
.kg-result.kg-tx__pill--rejected { background: rgba(239,68,68,.15);  color: #dc2626; }
.kg-result.kg-tx__pill--pending  { background: rgba(251,191,36,.18); color: #b45309; }
.kg-task__money { margin-left: auto; color: #4a3d66; font-size: 12px; }
.kg-task__money strong { color: #2a1a3f; font-weight: 800; margin-left: 4px; }
.kg-task__foot { display: flex; flex-direction: column; gap: 3px; margin-top: 10px; font-size: 11px; }
.kg-task__foot > div { display: flex; justify-content: space-between; gap: 10px; }
.kg-task__foot span { color: #8273a0; }
.kg-task__foot strong { color: #2a1a3f; font-weight: 600; }

.km-empty { text-align: center; padding: 60px 20px; color: #6b5c85; }
.km-empty__emoji { font-size: 50px; margin-bottom: 10px; }
.km-empty h3 { margin: 0 0 6px; font-size: 16px; color: #2a1a3f; }
.km-empty p { margin: 0; font-size: 13px; }

::v-deep .van-pull-refresh__head * { color: #6b5c85; font-size: 13px; }

@media (min-width: 720px) {
  .kg-tabs { top: 64px; padding: 12px 24px; }
}
</style>
